import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { BarChart } from '@mui/x-charts/BarChart'

import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import { Box, Button, ButtonGroup, IconButton, MenuItem, Select, Tooltip, Typography } from '@mui/material'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import moment from 'moment-timezone'
import { enqueueSnackbar } from 'notistack'

import { AppContext } from '/providers/app-context'
import { getCreditUsage } from '/providers/clients-api'

import DataExportDialog from '/components/data-export-dialog'
import StyledPaper from '../paper.jsx'
import { createTableStyles } from '../table-styles'
import excelExport from './credit-usage-to-excel'

const periodOptions = [
  { label: '1 day', value: 1 },
  { label: '1 week', value: 7 },
  { label: '1 month', value: 31 },
  { label: '3 months', value: 92 },
  { label: '6 months', value: 183 },
  { label: '12 months', value: 365 }
]

const chartSetting = {
  yAxis: [
    {
      label: 'credits',
      width: 90,
    },
  ],
}

const CreditUsage = () => {
  const theme = useTheme()
  const tableStyles = useMemo(() => createTableStyles(theme), [theme])
  const { apiSource, apiKey } = useContext(AppContext)

  const [viewMode, setViewMode] = useState('table')
  const [periodDays, setPeriodDays] = useState(31)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)

  const requestIdRef = useRef(0)

  useEffect(() => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    const loadUsage = async () => {
      setRows([])
      setLoading(true)

      let offset = 0
      const limit = 250
      let hasMore = true
      let isFirstBatch = true

      while (hasMore && requestIdRef.current === requestId) {
        if (!isFirstBatch) setLoadingMore(true)

        const result = await getCreditUsage({ apiSource, apiKey, period_days: periodDays, offset, limit })

        if (!result.ok) {
          enqueueSnackbar({ message: result.error, variant: 'error' })
          hasMore = false
          break
        }

        setRows(current => [...current, ...(result.usage || [])])
        hasMore = result.hasMore
        offset += limit
        isFirstBatch = false
      }

      if (requestIdRef.current === requestId) {
        setLoading(false)
        setLoadingMore(false)
      }
    }

    loadUsage()

    return () => {
      requestIdRef.current += 1
    }
  }, [apiSource, apiKey, periodDays])

  const columns = useMemo(() => [
    {
      accessorKey: 'ts',
      header: 'Timestamp',
      size: 170,
      Cell: ({ cell }) => moment(cell.getValue(), "YYYYMMDDHHmmss").format("DD MMM YYYY HH:mm")
    },
    {
      accessorKey: 'event_type',
      header: 'Event Type',
      size: 160,
    },
    {
      accessorKey: 'direction',
      header: 'Direction',
      size: 100,
      Cell: ({ cell }) => cell.getValue() === 'topup' ? 'Credits added' : 'Credits spent'
    },
    {
      accessorKey: 'template_name',
      header: 'Template',
      size: 220,
      Cell: ({ row }) => row.original.template_name || row.original.template_id || '-'
    },
    {
      accessorKey: 'bundle_name',
      header: 'Bundle',
      size: 140,
      Cell: ({ cell }) => cell.getValue() || '-'
    },
    {
      accessorKey: 'num_passes',
      header: 'Passes',
      size: 90,
      Cell: ({ cell }) => cell.getValue() ?? '-'
    },
    {
      accessorKey: 'num_credits',
      header: 'Credits',
      size: 100,
      Cell: ({ row }) => {
        const sign = row.original.direction === 'topup' ? '+' : '-'
        return `${sign}${Number(row.original.num_credits || 0).toLocaleString()}`
      }
    },
    {
      accessorKey: 'price',
      header: 'Price',
      size: 100,
      Cell: ({ cell }) => cell.getValue() != null ? `£${Number(cell.getValue()).toFixed(2)}` : '-'
    },
    {
      accessorKey: 'vat',
      header: 'VAT',
      size: 100,
      Cell: ({ cell }) => cell.getValue() != null ? `£${Number(cell.getValue()).toFixed(2)}` : '-'
    }
  ], [])

  const table = useMaterialReactTable({
    columns,
    data: rows,
    state: {
      isLoading: loading
    },
    initialState: {
      showColumnFilters: false,
      sorting: [{ id: 'ts', desc: true }]
    },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: originalRow => originalRow.row_id,
    ...tableStyles,
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
  })

  const chartData = useMemo(() => {
    const grouped = rows.reduce((acc, row) => {
      const key = row.event_type || 'unknown'
      if (!acc[key]) acc[key] = { event_type: key, spent: 0, added: 0 }

      if (row.direction === 'topup') {
        acc[key].added += Number(row.num_credits || 0)
      } else {
        acc[key].spent += Number(row.num_credits || 0)
      }

      return acc
    }, {})

    return Object.values(grouped).sort((a, b) => (b.spent + b.added) - (a.spent + a.added))
  }, [rows])

  return (
    <>
      <div className='flex flex-col gap-5'>
        <StyledPaper>
          <div className='flex flex-row justify-between items-center p-5'>
            <div className='flex flex-row gap-4 items-center'>
              <Typography variant='h5'>Credit Usage</Typography>
              <Select size='small' value={periodDays} onChange={e => setPeriodDays(Number(e.target.value))}>
                {periodOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
              <ButtonGroup>
                <Button variant={viewMode === 'table' ? 'contained' : 'outlined'} onClick={() => setViewMode('table')}>Table</Button>
                <Button variant={viewMode === 'chart' ? 'contained' : 'outlined'} onClick={() => setViewMode('chart')}>Chart</Button>
              </ButtonGroup>
            </div>
            <div className='text-sm text-gray-500'>
              {loading ? 'Loading usage data...' : loadingMore ? `Loaded ${rows.length.toLocaleString()} rows so far...` : `${rows.length.toLocaleString()} rows loaded`}
            </div>
          </div>
        </StyledPaper>

        {viewMode === 'table' ? (
          <Box sx={{ height: 'calc(100vh - 245px)', width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <StyledPaper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} height="100vh">
              <MaterialReactTable table={table} />
            </StyledPaper>
          </Box>
        ) : (
          <StyledPaper>
            <div className='p-5'>
              <Typography variant='h6'>Credits by event type</Typography>
              <Box sx={{ height: 520, width: '100%' }}>
                <BarChart
                  dataset={chartData}
                  xAxis={[{ scaleType: 'band', dataKey: 'event_type' }]}
                  series={[
                    { dataKey: 'spent', label: 'Credits spent' },
                    { dataKey: 'added', label: 'Credits added' }
                  ]}
                  {...chartSetting}
                />
              </Box>
            </div>
          </StyledPaper>
        )}
      </div>

      <DataExportDialog
        open={dataExportDialogOpen}
        setOpen={setDataExportDialogOpen}
        fileName={`credit-usage-${periodDays}-days`}
        data={rows}
        excelExport={excelExport}
      />
    </>
  )
}

export default CreditUsage
